import { jsPDF } from "jspdf";
import {
  isEncryptedDocumentUrl,
  decryptBlob,
} from "../../../utils/documentEncryption";

/**
 * For encrypted document URLs (.enc), fetch and decrypt to a data URL for PDF embedding.
 */
const loadEncryptedImageAsBase64 = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch");
  const blob = await res.blob();
  const decrypted = await decryptBlob(blob);
  if (!decrypted) throw new Error("Decryption failed");
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(decrypted);
  });
};

/**
 * Helper function to load image and convert to base64
 */
const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      try {
        const base64 = canvas.toDataURL("image/jpeg", 0.8);
        resolve(base64);
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Generates a Survey Report PDF for a customer
 * @param {Object} customer - Customer object
 * @param {Object} survey - Survey data
 * @param {Object} region - Region object
 * @param {Object} cluster - Cluster object
 * @returns {Promise<void>}
 */
export const generateSurveyReport = async (
  customer,
  survey,
  region = null,
  cluster = null
) => {
  try {
    // Create PDF
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;

    // Color scheme - brand green
    const colors = {
      primary: [19, 105, 63], // #13693F Light green
      secondary: [19, 105, 63], // #13693F Light green
      accent: [19, 105, 63], // #13693F Light green
      error: [231, 76, 60], // Red
      text: [44, 62, 80], // Dark text
      lightGray: [236, 240, 241],
      border: [189, 195, 199],
    };

    // Helper function to add new page if needed
    const checkPageBreak = (requiredHeight) => {
      if (yPos + requiredHeight > pageHeight - margin) {
        pdf.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };

    // Helper function to add section title with styling
    const addSectionTitle = (title, number) => {
      const sectionTopGap = 6;
      // add a little breathing room before every section (except when at top margin)
      if (yPos > margin) {
        checkPageBreak(20 + sectionTopGap);
        yPos += sectionTopGap;
      } else {
        checkPageBreak(20);
      }

      // Background box
      pdf.setFillColor(...colors.lightGray);
      pdf.roundedRect(margin - 2, yPos - 5, pageWidth - (margin * 2) + 4, 12, 2, 2, "F");

      // Section number badge
      pdf.setFillColor(...colors.primary);
      pdf.setDrawColor(...colors.primary);
      pdf.circle(margin + 5, yPos + 1, 4, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "bold");
      pdf.text(number.toString(), margin + 5, yPos + 2.5, { align: "center" });

      // Title
      pdf.setFontSize(13);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...colors.text);
      pdf.text(title, margin + 12, yPos + 2);

      yPos += 12;
    };

    // Helper function to add label-value pair
    const addLabelValue = (label, value, indent = 0) => {
      checkPageBreak(8);
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...colors.secondary);
      pdf.text(label + ":", margin + indent, yPos);

      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...colors.text);
      const valueText = value || "N/A";
      const lines = pdf.splitTextToSize(valueText, pageWidth - margin * 2 - indent - 40);
      pdf.text(lines, margin + indent + 35, yPos);
      yPos += lines.length * 5 + 3;
    };

    const formatBool = (value) => {
      if (value === null || value === undefined) return "N/A";
      return value ? "Yes" : "No";
    };

    const formatValue = (value) => {
      if (value === null || value === undefined || value === "") return "N/A";
      if (typeof value === "boolean") return value ? "Yes" : "No";
      return String(value).split("_").join(" ").toUpperCase();
    };

    // Helper to render a simple 2-column table (Field / Value)
    const addKeyValueTable = (rows) => {
      if (!rows || rows.length === 0) return;

      const tableX = margin;
      const tableWidth = pageWidth - margin * 2;
      const labelColWidth = Math.min(65, Math.max(52, tableWidth * 0.38));
      const valueColWidth = tableWidth - labelColWidth;
      const lineHeight = 4.5;
      const cellPaddingX = 2;
      const cellPaddingY = 3.5;

      // Header row
      checkPageBreak(12);
      pdf.setFillColor(...colors.primary);
      pdf.setDrawColor(...colors.border);
      pdf.setLineWidth(0.2);
      pdf.rect(tableX, yPos, tableWidth, 8, "FD");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(255, 255, 255);
      pdf.text("Field", tableX + cellPaddingX, yPos + 5.2);
      pdf.text("Value", tableX + labelColWidth + cellPaddingX, yPos + 5.2);
      yPos += 8;

      // Body rows
      pdf.setFontSize(8.5);
      for (let i = 0; i < rows.length; i++) {
        const [rawLabel, rawValue] = rows[i];
        const label = formatValue(rawLabel);
        const value = formatValue(rawValue);

        const labelLines = pdf.splitTextToSize(label, labelColWidth - cellPaddingX * 2);
        const valueLines = pdf.splitTextToSize(value, valueColWidth - cellPaddingX * 2);
        const contentLines = Math.max(labelLines.length, valueLines.length);
        const rowHeight = cellPaddingY + contentLines * lineHeight + 1.5;

        checkPageBreak(rowHeight + 2);

        // Label cell (light background)
        pdf.setFillColor(...colors.lightGray);
        pdf.setDrawColor(...colors.border);
        pdf.rect(tableX, yPos, labelColWidth, rowHeight, "FD");

        // Value cell (white)
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(...colors.border);
        pdf.rect(tableX + labelColWidth, yPos, valueColWidth, rowHeight, "FD");

        // Text (black for section tables)
        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "bold");
        pdf.text(labelLines, tableX + cellPaddingX, yPos + cellPaddingY + 0.5);

        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "normal");
        pdf.text(valueLines, tableX + labelColWidth + cellPaddingX, yPos + cellPaddingY + 0.5);

        yPos += rowHeight;
      }

      yPos += 4;
    };

    // Helper to render a bullet list (e.g. selected obstacles)
    const addBulletList = (items) => {
      if (!items || items.length === 0) return;
      const lineHeight = 5;
      checkPageBreak(items.length * lineHeight + 4);
      pdf.setFontSize(8.5);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(0, 0, 0);
      for (const item of items) {
        pdf.text(`• ${item}`, margin, yPos + 4);
        yPos += lineHeight;
      }
      yPos += 4;
    };

    // Helper function to add images in a grid
    const addImageGrid = async (images, title, maxPerRow = 2) => {
      if (!images || images.length === 0) return;

      checkPageBreak(50);

      // Title for image section
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...colors.secondary);
      pdf.text(title, margin, yPos);
      yPos += 6;

      const imageWidth = (pageWidth - margin * 2 - (maxPerRow - 1) * 5) / maxPerRow;
      const imageHeight = imageWidth * 0.75; // 4:3 aspect ratio

      for (let i = 0; i < images.length; i++) {
        if (i > 0 && i % maxPerRow === 0) {
          yPos += imageHeight + 8;
          checkPageBreak(imageHeight + 10);
        }

        const col = i % maxPerRow;
        const x = margin + col * (imageWidth + 5);
        const currentY = yPos;

        try {
          const base64Image = await loadImageAsBase64(images[i]);

          // Add border
          pdf.setDrawColor(...colors.border);
          pdf.setLineWidth(0.5);
          pdf.rect(x, currentY, imageWidth, imageHeight);

          // Add image
          pdf.addImage(base64Image, "JPEG", x + 1, currentY + 1, imageWidth - 2, imageHeight - 2);
        } catch (error) {
          console.error(`Error loading image ${images[i]}:`, error);
          // Add placeholder
          pdf.setFillColor(...colors.lightGray);
          pdf.rect(x, currentY, imageWidth, imageHeight, "F");
          pdf.setFontSize(8);
          pdf.setTextColor(150, 150, 150);
          pdf.text("Image not available", x + imageWidth / 2, currentY + imageHeight / 2, {
            align: "center",
          });
        }
      }

      yPos += imageHeight + 8;
    };

    // Helper function to add labeled images in a grid (2-up by default)
    const addLabeledImageGrid = async (items, title, maxPerRow = 2) => {
      if (!items || items.length === 0) return;

      checkPageBreak(50);

      // Title for image section
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...colors.secondary);
      pdf.text(title, margin, yPos);
      yPos += 6;

      const imageWidth = (pageWidth - margin * 2 - (maxPerRow - 1) * 5) / maxPerRow;
      const imageHeight = imageWidth * 0.75; // 4:3
      const captionMaxWidth = imageWidth;
      const captionLineHeight = 4;
      const captionTopGap = 2;

      for (let i = 0; i < items.length; i++) {
        if (i > 0 && i % maxPerRow === 0) {
          // move to next row
          yPos += imageHeight + 12;
          checkPageBreak(imageHeight + 18);
        }

        const col = i % maxPerRow;
        const x = margin + col * (imageWidth + 5);
        const currentY = yPos;

        const label = items[i]?.label ? String(items[i].label) : "";
        const url = items[i]?.url;
        const useDecrypt = items[i]?.encrypted === true;

        try {
          const base64Image = useDecrypt
            ? await loadEncryptedImageAsBase64(url)
            : await loadImageAsBase64(url);
          pdf.setDrawColor(...colors.border);
          pdf.setLineWidth(0.5);
          pdf.rect(x, currentY, imageWidth, imageHeight);
          pdf.addImage(base64Image, "JPEG", x + 1, currentY + 1, imageWidth - 2, imageHeight - 2);
        } catch (error) {
          console.error(`Error loading image ${url}:`, error);
          pdf.setFillColor(...colors.lightGray);
          pdf.rect(x, currentY, imageWidth, imageHeight, "F");
          pdf.setFontSize(8);
          pdf.setTextColor(150, 150, 150);
          pdf.text("Image not available", x + imageWidth / 2, currentY + imageHeight / 2, {
            align: "center",
          });
        }

        if (label) {
          pdf.setFontSize(8);
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor(...colors.text);
          const captionLines = pdf.splitTextToSize(label, captionMaxWidth);
          const captionY = currentY + imageHeight + captionTopGap + 2;
          pdf.text(captionLines, x, captionY);
        }
      }

      yPos += imageHeight + 12;
    };

    // Header with logo
    const logoPath = "/assets/images/brand.png";

    const logoBase64 = await loadImageAsBase64(logoPath);
    const logoWidth = 26;
    const logoHeight = 26;
    pdf.addImage(logoBase64, "PNG", margin, 4, logoWidth, logoHeight);

    // Header text (without blue background)
    pdf.setTextColor(...colors.text);
    pdf.setFontSize(20);
    pdf.setFont("helvetica", "bold");
    pdf.text("SURVEY REPORT", pageWidth / 2, 15, { align: "center" });

    // Decorative line below header
    pdf.setDrawColor(...colors.primary);
    pdf.setLineWidth(0.5);
    pdf.line(margin, 30, pageWidth - margin, 30);

    yPos = 40;

    // Report Date
    pdf.setTextColor(100, 100, 100);
    pdf.setFontSize(9);
    const reportDate = new Date().toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    pdf.text(`Report Generated: ${reportDate}`, pageWidth - margin, yPos, { align: "right" });
    yPos += 8;

    // Customer Information Section
    addSectionTitle("CUSTOMER INFORMATION", "1");
    addKeyValueTable([
      ["Customer Name", customer?.name],
      ["CIN (Customer ID)", customer?.cin],
      ["USN (Service Number)", customer?.usn],
      ["Phone Number", customer?.phone_number],
      ["Aadhaar Number", customer?.aadhaar_number],
      ["Date of Birth", customer?.dob ? new Date(customer.dob).toLocaleDateString("en-IN") : "N/A"],
      ["Address", customer?.address],
      ["Village", customer?.village],
      ["District", customer?.district],
      ["State", customer?.state],
      ["Pincode", customer?.pincode],
      ["Region", region?.name],
      ["Cluster", cluster?.name],
      [
        "Coordinates",
        customer?.latitude && customer?.longitude ? `${customer.latitude}, ${customer.longitude}` : "N/A",
      ],
    ]);

    yPos += 5;

    // Survey Details Sections (tabular, divided by survey steps)
    let sectionNo = 2;

    if (survey) {
      // Step: Survey Overview
      addSectionTitle("SURVEY OVERVIEW", String(sectionNo++));
      addKeyValueTable([
        ["Roof Feasibility Status", survey.roof_feasible === true ? "Feasible" : survey.roof_feasible === false ? "Not Feasible" : "N/A"],
        ["Rejection Reason", survey.roof_feasible === false ? formatValue(survey.roof_rejection_reason) : "N/A"],
        ["Survey Completed On", survey.submitted_at ? new Date(survey.submitted_at).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" }) : "N/A"],
      ]);

      // Step: Meter Details
      addSectionTitle("METER DETAILS", String(sectionNo++));
      addKeyValueTable([
        ["Proposed Rooftop Solar Capacity (kW)", survey.proposed_capacity_kw ? `${survey.proposed_capacity_kw} kW` : "N/A"],
        ["Sanctioned Meter Load (kW)", survey.sanctioned_load_kw ? `${survey.sanctioned_load_kw} kW` : "N/A"],
        ["Phase Connection", survey.phase_connection || "N/A"],
        ["Lineman Name", survey.lineman_name || "N/A"],
      ]);

      // Step: Roof Engineering
      addSectionTitle("ROOF ENGINEERING", String(sectionNo++));
      addKeyValueTable([
        ["Roof Type", survey.roof_type],
        ["Total Roof Area", survey.total_roof_area_sqft ? `${survey.total_roof_area_sqft} sq ft` : "N/A"],
        ["Shadow Free Area", survey.shadow_free_area_sqft ? `${survey.shadow_free_area_sqft} sq ft` : "N/A"],
        ["Roof Condition", survey.roof_condition],
        ["Roof Orientation", survey.roof_orientation],
        ["Parapet Height", survey.parapet_height_ft ? `${survey.parapet_height_ft} ft` : "N/A"],
        ["Number of Floors", survey.number_of_floors],
        ["Access Type", survey.access_type],
        ["Panel Placement", survey.panel_placement || "N/A"],
      ]);

      // Step: Electrical & Site Logistics
      addSectionTitle("ELECTRICAL & SITE LOGISTICS", String(sectionNo++));
      addKeyValueTable([
        ["Meter Location", survey.meter_location],
        ["Internet Status", survey.internet_status],
        ["Earthing Soil Type", survey.earthing_soil_type],
      ]);

      // Step: Obstacles — list only selected (checked) obstacles
      addSectionTitle("OBSTACLES", String(sectionNo++));
      const obstacleLabels = [
        [survey.no_obstacle, "No Obstacle"],
        [survey.water_tank_present, "Water Tank Present"],
        [survey.neighbors_wall_present, "Neighbor's Wall Present"],
        [survey.trees_present, "Trees Present"],
      ]
        .filter(([val]) => val === true)
        .map(([, label]) => label);
      addBulletList(obstacleLabels.length ? obstacleLabels : ["—"]);

      // Roof Inspection Photos (from Roof Inspection step)
      addSectionTitle("ROOF INSPECTION PHOTOS", String(sectionNo++));
      const roofInspectionPhotoItems = [];
      if (survey.roof_inspection_photos?.length > 0) {
        survey.roof_inspection_photos.forEach((url, idx) => {
          if (url) roofInspectionPhotoItems.push({ url, label: `Roof Inspection Photo ${idx + 1}` });
        });
      }
      if (roofInspectionPhotoItems.length > 0) {
        await addLabeledImageGrid(roofInspectionPhotoItems, "Roof Inspection Photos", 2);
      }

      // Categorized Site Photos (House Front View, Meter, etc.)
      addSectionTitle("SITE PHOTOS", String(sectionNo++));
      const sitePhotoItems = [];
      if (survey.house_front_view_photos?.length > 0) {
        survey.house_front_view_photos.forEach((url, idx) => {
          if (url) sitePhotoItems.push({ url, label: `House Front View Photo ${idx + 1}` });
        });
      }
      if (survey.meter_photos?.length > 0) {
        survey.meter_photos.forEach((url, idx) => {
          if (url) sitePhotoItems.push({ url, label: `Meter Photo ${idx + 1}` });
        });
      }
      if (survey.line_diagram_photos?.length > 0) {
        survey.line_diagram_photos.forEach((url, idx) => {
          if (url) sitePhotoItems.push({ url, label: `Line Diagram Photo ${idx + 1}` });
        });
      }
      if (survey.obstacle_photos?.length > 0) {
        survey.obstacle_photos.forEach((url, idx) => {
          if (url) sitePhotoItems.push({ url, label: `Obstacle Photo ${idx + 1}` });
        });
      }
      await addLabeledImageGrid(sitePhotoItems, "Site Photos", 2);

      // Document Photos (may be encrypted .enc URLs)
      if (
        survey.aadhar_photo ||
        survey.bank_passbook_photo ||
        survey.electricity_bill_photo
      ) {
        addSectionTitle("DOCUMENT PHOTOS", String(sectionNo++));
        const docItems = [];
        const enc = (url) => ({
          url,
          label: "",
          encrypted: isEncryptedDocumentUrl(url),
        });
        if (survey.aadhar_photo) docItems.push({ ...enc(survey.aadhar_photo), label: "Aadhaar Card" });
        if (survey.bank_passbook_photo) docItems.push({ ...enc(survey.bank_passbook_photo), label: "Bank Passbook" });
        if (survey.electricity_bill_photo) docItems.push({ ...enc(survey.electricity_bill_photo), label: "Electricity Bill" });
        await addLabeledImageGrid(docItems, "Documents", 2);
      }

      // Tentative locations
      if (survey.tentative_panel_location_photo || survey.tentative_inverter_location_photo) {
        addSectionTitle("TENTATIVE LOCATIONS", String(sectionNo++));
        const tentativeItems = [];
        if (survey.tentative_panel_location_photo) {
          tentativeItems.push({ url: survey.tentative_panel_location_photo, label: "Panel Location" });
        }
        if (survey.tentative_inverter_location_photo) {
          tentativeItems.push({ url: survey.tentative_inverter_location_photo, label: "Inverter Location" });
        }
        await addLabeledImageGrid(tentativeItems, "Tentative Locations", 2);
      }

      // Signatures
      if (survey.surveyor_signature_photo || survey.customer_signature_photo) {
        addSectionTitle("SIGNATURES", String(sectionNo++));
        const signatureItems = [];
        if (survey.surveyor_signature_photo) {
          signatureItems.push({ url: survey.surveyor_signature_photo, label: "Surveyor Signature" });
        }
        if (survey.customer_signature_photo) {
          signatureItems.push({ url: survey.customer_signature_photo, label: "Customer Signature" });
        }
        await addLabeledImageGrid(signatureItems, "Signatures", 2);
      }

      // Consent
      addSectionTitle("CONSENT & SIGN-OFFS", String(sectionNo++));
      addKeyValueTable([
        ["Consent Accepted", formatBool(survey.consent_accepted)],
        ["Surveyor Signed", formatBool(survey.surveyor_signed)],
        ["Customer Signed", formatBool(survey.customer_signed)],
      ]);

      if (survey.survey_notes) {
        addSectionTitle("SURVEY NOTES", String(sectionNo++));
        addKeyValueTable([["Notes", survey.survey_notes]]);
      }

      // QR Code Photo
      if (survey.qr_code_photo) {
        addSectionTitle("QR CODE STICKER", String(sectionNo++));
        checkPageBreak(50);
        try {
          const base64Image = await loadImageAsBase64(survey.qr_code_photo);
          const imgWidth = 50;
          const imgHeight = 50;
          pdf.setDrawColor(...colors.border);
          pdf.rect(margin, yPos, imgWidth, imgHeight);
          pdf.addImage(base64Image, "JPEG", margin, yPos, imgWidth, imgHeight);
          yPos += imgHeight + 5;
        } catch (error) {
          console.error("Error loading QR code photo:", error);
          pdf.setFillColor(...colors.lightGray);
          pdf.rect(margin, yPos, 50, 50, "F");
          pdf.setFontSize(8);
          pdf.setTextColor(150, 150, 150);
          pdf.text("QR Code not available", margin + 25, yPos + 25, { align: "center" });
          yPos += 55;
        }
      }
    } else {
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(150, 150, 150);
      pdf.text("No survey information available", margin, yPos);
      yPos += 8;
    }

    yPos += 10;

    // Footer - Survey Statement
    checkPageBreak(30);
    pdf.setFillColor(...colors.lightGray);
    pdf.roundedRect(margin, yPos, pageWidth - margin * 2, 25, 2, 2, "F");

    pdf.setFontSize(11);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...colors.primary);
    pdf.text("SURVEY STATEMENT", pageWidth / 2, yPos + 6, { align: "center" });
    yPos += 10;

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...colors.text);
    const surveyText = `This survey report documents the site inspection and assessment for the solar installation project. The roof inspection status, site conditions, and all relevant photographs have been recorded as part of this survey.`;
    const surveyLines = pdf.splitTextToSize(surveyText, pageWidth - margin * 2 - 6);
    pdf.text(surveyLines, margin + 3, yPos);
    yPos += surveyLines.length * 5 + 8;

    // Signature line
    // Add a little top margin so lines don't feel cramped
    const signatureTopGap = 6;
    checkPageBreak(signatureTopGap + 6);
    yPos += signatureTopGap;

    pdf.setDrawColor(...colors.border);
    pdf.line(margin, yPos, margin + 60, yPos);
    pdf.setFontSize(8);
    pdf.setTextColor(...colors.secondary);
    pdf.text("Surveyor Signature", margin, yPos - 2);

    pdf.line(pageWidth - margin - 60, yPos, pageWidth - margin, yPos);
    pdf.text("Date", pageWidth - margin - 30, yPos - 2, { align: "center" });

    // Save PDF
    const customerIdentifier = customer?.cin || customer?.usn || customer?.id?.slice(0, 8) || "customer";
    const timestamp = new Date().toISOString().split("T")[0];
    pdf.save(`Survey_Report_${customerIdentifier}_${timestamp}.pdf`);
  } catch (error) {
    console.error("Error generating survey report:", error);
    throw error;
  }
};
